import { BASE_URL } from "../utils/config";
import axios, { AxiosError } from "axios";
import { deleteSessionCookie, getSessionCookie, setSessionCookie } from "../utils/cookies";
import { APP_VERSION, BUILD_NO } from "../lib/constants/constant";
import { browserName, deviceType, osName } from "react-device-detect";

export const apiClient = axios.create({
    baseURL: BASE_URL,
});

// ─── Refresh Token Queue ───────────────────────────────────────────────────────
let isRefreshing = false;
let failedQueue: { resolve: (token: string) => void; reject: (error: any) => void }[] = [];

const processQueue = (error: any, token: string | null = null) => {
    failedQueue.forEach((promise) => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token!);
        }
    });
    failedQueue = [];
};

const clearSessionAndRedirect = () => {
    deleteSessionCookie("accessToken");
    deleteSessionCookie("refreshToken");
    if (window.location.pathname !== "/login") {
        window.location.href = "/login";
    }
};

// ─── Request Interceptor ───────────────────────────────────────────────────────
apiClient.interceptors.request.use((config) => {
    const accessToken = getSessionCookie("accessToken");

    let deviceId = localStorage.getItem("device_id_key");
    if (!deviceId) {
        deviceId = Math.random().toString(36).substring(2, 15);
        localStorage.setItem("device_id_key", deviceId);
    }

    config.headers = config.headers || {};
    if (accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
    }
    config.headers["x-build-number"] = BUILD_NO;
    config.headers["x-app-version"] = APP_VERSION;
    config.headers["x-device-id"] = deviceId;
    config.headers["x-platform"] = `${osName}/${browserName}/${deviceType}`;
    config.headers["ngrok-skip-browser-warning"] = "any";

    return config;
});

// ─── Response Interceptor ──────────────────────────────────────────────────────
apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
        const originalRequest = error.config as any;

        if (error?.response?.status !== 401) {
            return Promise.reject(error);
        }

        // If already retried once, don't retry again
        if (originalRequest._retry) {
            return Promise.reject(error);
        }

        if (isRefreshing) {
            // Queue this request until the refresh is done
            return new Promise((resolve, reject) => {
                failedQueue.push({ resolve, reject });
            })
                .then((token) => {
                    originalRequest.headers.Authorization = `Bearer ${token}`;
                    return apiClient(originalRequest);
                })
                .catch((err) => Promise.reject(err));
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
            const refreshToken = getSessionCookie("refreshToken");
            console.log("Attempting token refresh with refresh token:", refreshToken);
            if (!refreshToken) {
                console.log("unable to get token")
                clearSessionAndRedirect();
                return Promise.reject(error);
            }

            const res = await axios.post(
                `${BASE_URL}/auth/refresh`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${refreshToken}`,
                    },
                }
            );

            if (res?.data?.accessToken) {
                setSessionCookie("accessToken", res.data.accessToken);
                setSessionCookie("refreshToken", res.data.refreshToken);

                // Update the failed request and process the queue
                originalRequest.headers.Authorization = `Bearer ${res.data.accessToken}`;
                processQueue(null, res.data.accessToken);

                return apiClient(originalRequest);
            } else {
                processQueue(error, null);
                clearSessionAndRedirect();
                return Promise.reject(error);
            }
        } catch (refreshError) {
            processQueue(refreshError, null);
            clearSessionAndRedirect();
            return Promise.reject(refreshError);
        } finally {
            isRefreshing = false;
        }
    }
);